import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, LayoutList, MessagesSquare, Sparkles, Wrench, ClipboardCheck, Building2 } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/motion";
import { Container, SectionHeading } from "@/components/Section";
import { IMAGES, SERVICES } from "@/data/content";

const ICONS = { TrendingUp, LayoutList, MessagesSquare, Sparkles, Wrench, ClipboardCheck };

export default function Services() {
  return (
    <main data-testid="services-page">
      <section className="noise-dark bg-forest pb-24 pt-44 text-cream sm:pb-32 sm:pt-52">
        <Container className="relative z-10">
          <FadeUp>
            <p className="eyebrow text-sage">Services</p>
          </FadeUp>
          <MaskedLines
            className="mt-6 max-w-4xl"
            lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06]"
            lines={[
              <span key="1">End-to-end property care,</span>,
              <span key="2" className="italic text-sage-light">run like a business.</span>,
            ]}
          />
          <FadeUp delay={0.35}>
            <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-cream/70">
              Six pillars of management, delivered by one accountable Dublin team. Everything a successful
              short-term rental needs — and nothing your diary does.
            </p>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-cream py-24 sm:py-32">
        <Container className="space-y-20">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const flip = i % 2 === 1;
            return (
              <FadeUp key={s.title}>
                <div
                  data-testid={`service-detail-${i}`}
                  className={`grid items-center gap-10 rounded-2xl border border-forest/10 bg-bone p-9 sm:p-12 lg:grid-cols-12 ${
                    flip ? "" : ""
                  }`}
                >
                  <div className={`lg:col-span-4 ${flip ? "lg:order-2" : ""}`}>
                    <div className="flex items-center gap-5">
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-forest text-sage">
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className="font-mono text-sm text-forest/35">{String(i + 1).padStart(2, "0")} / 06</span>
                    </div>
                    <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-medium text-forest">{s.title}</h2>
                  </div>
                  <div className="lg:col-span-8">
                    <p className="text-base sm:text-lg font-medium leading-relaxed text-charcoal">{s.desc}</p>
                    <p className="mt-4 text-sm sm:text-base leading-relaxed text-charcoal/65">{s.detail}</p>
                  </div>
                </div>
              </FadeUp>
            );
          })}
        </Container>
      </section>

      <section className="noise-dark bg-forest-900 py-24 text-cream sm:py-32">
        <Container className="relative z-10 grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeading
              dark
              eyebrow="Corporate stays · 21+ days"
              title="A specialist service for longer corporate stays."
              copy="FieldGreen places and manages corporate stays of 21 days or more — professionals, project teams and relocating staff who need a dependable Dublin base. For owners, that means fewer turnovers and vetted, professional guests. For companies, it means one call and a property that's simply ready."
            />
            <FadeUp delay={0.2}>
              <div className="mt-9 flex flex-wrap gap-3">
                {["Vetted professional guests", "Fewer turnovers", "Flexible terms", "Fully managed"].map((t) => (
                  <span key={t} className="rounded-full border border-sage/25 bg-sage/10 px-4 py-2 text-xs font-medium text-sage-light">
                    {t}
                  </span>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.3}>
              <Link
                to="/assessment"
                data-testid="services-corporate-cta"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
              >
                Discuss a Corporate Stay Strategy
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img
                src={IMAGES.livingRoom}
                alt="A modern Dublin apartment prepared for a corporate stay"
                className="h-80 w-full object-cover lg:h-[420px]"
                data-testid="services-corporate-image"
              />
            </div>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-bone py-24 sm:py-32">
        <Container className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <FadeUp>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium text-forest">
                See what this could look like for your property.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-charcoal/65">
                A short conversation and a realistic assessment — that's all it takes to know whether FieldGreen
                makes sense for you.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <Link
              to="/assessment"
              data-testid="services-bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-7 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-forest-700"
            >
              Get a Property Assessment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </Container>
      </section>
    </main>
  );
}
